// Lead Types
export enum LeadStage {
  HOT = 'Hot',
  WARM = 'Warm',
  COLD = 'Cold',
  LOST = 'Lost',
  WON = 'Won'
}

export enum LeadSource {
  DIRECT = 'Direct',
  REFERRAL = 'Referral',
  WEBSITE = 'Website',
  EMAIL = 'Email Campaign',
  TRADE_SHOW = 'Trade Show',
  OTHER = 'Other'
}

export interface Lead {
  id: string
  companyName: string
  customerName: string
  address: string
  city: string
  pinCode: string
  contactNumber: string
  email: string
  htscNumber?: string
  edcName?: string
  sanctionedDemand: number
  demandUnit?: 'KW' | 'MW'          // L-05: unit alongside numeric demand
  drawlVoltage: string
  typeOfPower?: string               // from Type of Power master (Solar / Wind / Hybrid …)
  modeOfSupply?: string              // from Mode of Supply master (Third Party / Group Captive)
  requiredUnitsPerAnnum?: number     // expected annual consumption / generation (kWh)
  leadType: 'Consumer' | 'Generator' // BRD: only two types
  leadSource: LeadSource | string    // L-04: manual entry
  stage: LeadStage
  convertedToProposal?: boolean
  assignedTo?: string
  lostReason?: string               // L-03: required when marking Lost
  lostNotes?: string                // L-03: notes for future reactivation
  // ── Company duplicate-detection / GST verification ──────────────────
  gstNumber?: string
  gstStatus?: 'pending' | 'verified' // 'pending' while HTSC and/or GST are still missing
  gstVerifyDueDate?: string          // ISO date (YYYY-MM-DD) — 2 days after lead creation
  isDeactive?: boolean               // true once HTSC/GST has been missing past its 2-day update window — a flag only, it never blocks a stage or a proposal. See computeLeadDeactiveState in utils/gst.ts
  deactiveReason?: string            // explanation for deactive status (e.g. Missing HTSC / GST)
  missingFields?: string[]           // which of HTSC Number / GST Number are still outstanding
  daysLeft?: number                  // days remaining in the 2-day update window (undefined once nothing is missing)
  createdAt: Date
  updatedAt: Date
  discussionHistory: Discussion[]
}

export interface Discussion {
  id: string
  type: 'whatsapp' | 'email' | 'sms' | 'call' | 'note' | 'visit' | 'video' | 'expomeet'
  content: string
  createdBy: string
  createdAt: Date
  attachments?: string[]
  location?: { lat: number; lng: number }
  // ── DCR round-trip (set when the discussion was logged via a Daily Call Report) ──
  customerStatus?: string
  nextFollowUpDate?: string
  fromDcr?: boolean
}

// Proposal Types
export enum ProposalStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  UNDER_REVIEW = 'Under Review',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  EXPIRED = 'Expired',
  WON = 'Won',
  LOST = 'Lost'
}

export enum SupplySource {
  WIND_SOLAR = 'Wind & Solar',
  BIOMASS = 'Biomass',
  COAL = 'Coal',
  HYDRO = 'Hydro'
}

export interface PricingDetail {
  supplySource: SupplySource | string
  volumePerAnnum: string // e.g., "2.13 Crores"
  drawlVoltage: string
  rate: number
  equityInvestment: string // "NIL" or amount
  securityMechanism: string // "Nil" or type
}

export interface ContractTerms {
  contractPeriod: number // years
  lockInPeriod: number // years
  noticePeriod: number // months
  commencementMonth?: string
}

export interface BillingTerms {
  billRaiseMethod: string // "based on TANGEDCO Energy Allocation statement"
  paymentDueInDays: number
  minimumAssuredSupply: string // "80 to 85%"
  rateRevision: string // "50-50 basis"
}

export interface RequiredDocument {
  id: string
  description: string
  isMandatory: boolean
}

export interface SigningDocument {
  id: string
  name: string
  description?: string
}

export interface Proposal {
  id: string
  leadId: string
  revisionNumber: number
  referenceNumber?: string
  offerDate?: Date

  // Pricing & Commercial
  pricingDetails: PricingDetail[]
  powerFacilitationCharge?: number // per unit
  gstOnFacilitationCharge?: number // percentage
  deliveredTariff?: number

  // Original fields (maintained for compatibility)
  tariff: number
  sakthiTariff?: number
  commercialTerms: string
  gstDetails: {
    hsn: string
    sac: string
    placeOfSupply: string
    reverseCharge: boolean
  }

  // Contract & Terms
  contractTerms?: ContractTerms
  billingTerms?: BillingTerms

  // Energy Supply
  energySupplyAdjustments?: string // details about peak/normal/off-peak

  // Required Documents
  requiredDocuments?: RequiredDocument[]

  // Documents to Sign
  documentsToSign?: SigningDocument[]

  // Additional Info
  additionalTerms?: string
  notes?: string

  // Template Overrides/Customizations
  bodyHtml?: string
  companyContentHtml?: string
  coverPageEnabled?: boolean
  coverTitle?: string
  coverSubtitle?: string
  coverDescription?: string
  coverImage?: string
  termsConditionsHtml?: string

  validity: 15 | 30
  status: ProposalStatus
  pdfUrl?: string
  approvalChain: ApprovalStep[]
  createdAt: Date
  updatedAt: Date
  expiryDate: Date
  // Negotiation history — one entry per edit made to an already-Approved
  // proposal (via the "Negotiate" action), recording exactly which fields
  // changed and by whom. Empty/undefined for proposals never renegotiated.
  changeLog?: ProposalChangeLogEntry[]
}

export interface ProposalFieldChange {
  field: string
  label: string
  oldValue: string
  newValue: string
}

export interface ProposalChangeLogEntry {
  date: string        // ISO timestamp
  changedBy: string
  changes: ProposalFieldChange[]
}

export interface ApprovalStep {
  id: string
  role: string
  status: 'pending' | 'approved' | 'rejected'
  approvedBy?: string
  approvalDate?: string                          // ISO string — round-trips through JSON/DB
  comments?: string
  // ── Populated when the chain is built from the Role master's approval matrix ──
  stepNo?: number                                // order in the chain (1, 2, 3 …)
  stepName?: string                              // e.g. "Sales Manager Approval"
  approverName?: string                          // assigned approver employee name
  isRequired?: boolean                           // must approve for the proposal to pass
  action?: 'approve' | 'reject' | 'both'         // what this step's approver may do
}

// Agreement Types
export enum AgreementTemplate {
  CONSUMER = 'Consumer Agreement',
  GENERATOR = 'Generator Agreement',
  TRIPARTITE = 'Tripartite Agreement'
}

export enum AgreementStatus {
  DRAFT = 'Draft',
  READY_FOR_SIGNING = 'Ready for Signing',
  PENDING_SCAN = 'Pending Scan Upload',
  SIGNED = 'Signed',
  ACTIVE = 'Active',
  EXPIRED = 'Expired',
  AMENDED = 'Amended'
}

export interface Agreement {
  id: string
  code?: string                // human-readable Agreement Code (AGR###)
  proposalId: string
  leadId: string
  templateType: AgreementTemplate
  status: AgreementStatus
  generatedAt: Date
  signedAt?: Date
  startDate: Date               // the Agreement Date (date the agreement was entered into / executed)
  endDate: Date
  validityDays?: number        // contract validity period, in days
  renewalReminder: number // days before expiry
  stampPaperValue?: number    // ₹ denomination the agreement is executed on
  stampPaperNumber?: string   // stamp paper's own reference/serial number
  placeOfExecution?: string
  notes?: string               // special clauses / free-form notes
  signedCopyUrl?: string
  amendments: AgreementAmendment[]
  createdAt: Date
  updatedAt: Date
}

export interface AgreementAmendment {
  id: string
  revisionNumber: number
  changeDescription: string
  price?: number
  modeOfSupply?: string
  typeOfPower?: string
  /** Period the amendment is valid for (amendment applies between these dates). */
  validFrom?: Date
  validTo?: Date
  signedCopyUrl?: string
  signedAt?: Date
  createdAt: Date
}

// Generator-Consumer Mapping Types
export interface GeneratorConsumerMapping {
  id: string
  generatorId: string
  consumerId: string
  shareHolding: number // percentage 0-100
  tariff: number
  tradeMargin: number
  tariffHistory: TariffRecord[]
  createdAt: Date
  updatedAt: Date
}

export interface TariffRecord {
  date: Date
  tariff: number
  reason?: string
}

// Meter Reading Types
export enum MeterReadingStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  UNDER_REVIEW = 'Under Review',
  APPROVED = 'Approved',
  REJECTED = 'Rejected'
}

export interface MeterReading {
  id: string
  type: 'generator' | 'consumer'
  entityId: string // generator or consumer ID
  startDate: Date
  endDate: Date
  slots: SlotReading[]
  totalUnits: number
  status: MeterReadingStatus
  submittedBy: string
  submittedAt: Date
  approvedBy?: string
  approvedAt?: Date
  rejectionReason?: string
  createdAt: Date
  updatedAt: Date
}

export interface SlotReading {
  slotName: string // C1, C2, C3, etc.
  units: number
}

// Unit Allocation Types
export enum AllocationStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  UNDER_REVIEW = 'Under Review',
  APPROVED = 'Approved',
  LOCKED = 'Locked',
  REJECTED = 'Rejected'
}

export interface UnitAllocation {
  id: string
  generatorId: string
  meterReadingId: string
  allocations: ConsumerAllocation[]
  totalAllocated: number
  totalGenerated: number
  status: AllocationStatus
  submittedBy: string
  submittedAt: Date
  approvedBy?: string
  approvedAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface ConsumerAllocation {
  consumerId: string
  allocatedUnits: number
  percentage: number
  tariff: number
}

// Invoice Types
export enum InvoiceType {
  GENERATOR = 'Generator Invoice',
  CONSUMER = 'Consumer Invoice'
}

export enum InvoiceStatus {
  DRAFT = 'Draft',
  ISSUED = 'Issued',
  PARTIALLY_PAID = 'Partially Paid',
  PAID = 'Paid',
  OVERDUE = 'Overdue',
  CANCELLED = 'Cancelled'
}

export interface Invoice {
  id: string
  invoiceNumber: string
  type: InvoiceType
  entityId: string // generator or consumer ID
  allocationId: string
  issueDate: Date
  dueDate: Date
  amount: number
  gst: number
  totalAmount: number
  status: InvoiceStatus
  irn?: string // Invoice Reference Number for e-invoice
  ewayBill?: string
  gstr1Filed: boolean
  gstr3bFiled: boolean
  pdfUrl?: string
  notes?: string
  createdAt: Date
  updatedAt: Date
}

// Payment Types
export enum PaymentMode {
  CASH = 'Cash',
  CHEQUE = 'Cheque',
  NEFT = 'NEFT',
  RTGS = 'RTGS',
  UPI = 'UPI',
  IMPS = 'IMPS'
}

export interface Payment {
  id: string
  invoiceId: string
  amount: number
  mode: PaymentMode
  paymentDate: Date
  referenceNumber?: string
  receiptNumber: string
  tdsAmount?: number
  tdsCertificateRef?: string
  form16a?: string
  notes?: string
  createdAt: Date
  updatedAt: Date
}

// Generator Payout Types
export interface GeneratorPayout {
  id: string
  generatorId: string
  meterReadingId: string
  totalUnits: number
  tariff: number
  totalAmount: number
  tdsDeducted: number
  netPayout: number
  status: 'pending' | 'approved' | 'paid'
  paymentAdviceUrl?: string
  paymentDate?: Date
  createdAt: Date
  updatedAt: Date
}


// Master Data Types
export interface Tariff {
  id: string
  tariffCode: string
  amount: number
  effectiveFrom: Date
  description?: string
}

export interface GSTMaster {
  id: string
  percentage: number
  hsn: string
  sac: string
  description?: string
}

export interface SlotMaster {
  id: string
  slotName: string
  startTime: string
  endTime: string
  category: string
}

// Workflow Configuration
export interface WorkflowConfig {
  id: string
  module: string
  approvalChain: RoleApprover[]
}

export interface RoleApprover {
  sequence: number
  role: string
  canApprove: boolean
  canReject: boolean
}

// User Types
export enum UserRole {
  ADMIN = 'Admin',
  SALES_EXECUTIVE = 'Sales Executive',
  SALES_MANAGER = 'Sales Manager',
  DIRECTOR = 'Director',
  SUPERVISOR = 'Supervisor',
  ACCOUNTANT = 'Accountant',
  VIEWER = 'Viewer'
}

export interface User {
  id: string
  name: string
  email: string
  phone: string
  role: UserRole
  city?: string
  department?: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

// Dashboard Types
export interface DashboardStats {
  totalRevenue: number
  totalInvoiced: number
  totalExpenses: number
  netProfit: number
  leadConversionRate: number
  outstandingAmount: number
}

export interface ChartData {
  label: string
  value: number
  date?: Date
}
